import { EventEmitter } from 'events';
import { WhatsAppClient } from '../client/WhatsAppClient';

export interface PollingConfig {
  interval: number; // milliseconds
  batchSize: number;
  strategy: 'all-chats' | 'active-only' | 'unread-only' | 'smart';
  adaptiveInterval: boolean;
  maxAge: number; // max age for messages to sync (milliseconds)
  maxRetries: number;
  retryDelay: number;
  healthCheckInterval: number;
  enabled: boolean;
}

export interface PollingStats {
  totalPolls: number;
  successfulPolls: number;
  failedPolls: number;
  messagesProcessed: number;
  chatsProcessed: number;
  averageLatency: number;
  lastPollAt: Date | null;
  nextPollAt: Date | null;
  isRunning: boolean;
}

export interface ChatActivity {
  chatId: string;
  lastActivity: Date;
  messageCount: number;
  priority: number;
}

export class PollingService extends EventEmitter {
  private config: Required<PollingConfig>;
  private interval: NodeJS.Timeout | null = null;
  private healthCheckInterval: NodeJS.Timeout | null = null;
  private isPolling = false;
  private stats: PollingStats;
  private chatActivities: Map<string, ChatActivity> = new Map();
  private currentInterval: number;
  private retryCount = 0;

  constructor(
    private client: WhatsAppClient,
    private tel: string,
    config: Partial<PollingConfig> = {}
  ) {
    super();
    
    this.config = {
      interval: 5 * 60 * 1000, // 5 minutes
      batchSize: 50,
      strategy: 'smart',
      adaptiveInterval: true,
      maxAge: 24 * 60 * 60 * 1000, // 24 hours
      maxRetries: 3,
      retryDelay: 30000, // 30 seconds
      healthCheckInterval: 60000, // 1 minute
      enabled: true,
      ...config
    };

    this.currentInterval = this.config.interval;
    this.stats = this.initializeStats();
  }

  private initializeStats(): PollingStats {
    return {
      totalPolls: 0,
      successfulPolls: 0,
      failedPolls: 0,
      messagesProcessed: 0,
      chatsProcessed: 0,
      averageLatency: 0,
      lastPollAt: null,
      nextPollAt: null,
      isRunning: false
    };
  }

  /**
   * Start the polling service
   */
  start(): void {
    if (!this.config.enabled || this.interval) {
      return;
    }

    this.stats.isRunning = true;
    this.scheduleNextPoll();
    this.startHealthCheck();
    
    this.emit('polling:started', { tel: this.tel, interval: this.currentInterval });
  }

  /**
   * Stop the polling service
   */
  stop(): void {
    if (this.interval) {
      clearTimeout(this.interval);
      this.interval = null;
    }

    if (this.healthCheckInterval) {
      clearTimeout(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }

    this.stats.isRunning = false;
    this.stats.nextPollAt = null;
    this.isPolling = false;
    
    this.emit('polling:stopped', { tel: this.tel });
  }

  /**
   * Schedule the next poll
   */
  private scheduleNextPoll(): void {
    if (!this.config.enabled) {
      return;
    }

    const nextPollTime = Date.now() + this.currentInterval;
    this.stats.nextPollAt = new Date(nextPollTime);

    this.interval = setTimeout(async () => {
      await this.executePoll();
      this.scheduleNextPoll();
    }, this.currentInterval);
  }

  /**
   * Execute a single poll
   */
  private async executePoll(): Promise<void> {
    if (this.isPolling) {
      this.emit('polling:skipped', { reason: 'already_polling', tel: this.tel });
      return;
    }

    this.isPolling = true;
    const startTime = Date.now();
    this.stats.totalPolls++;
    this.stats.lastPollAt = new Date();

    try {
      this.emit('sync:start', { 
        tel: this.tel, 
        strategy: this.config.strategy,
        estimatedTime: this.calculateEstimatedTime()
      });

      // Get chats to process based on strategy
      const chatsToProcess = await this.getChatsToProcess();
      
      const syncResult = await this.syncChats(chatsToProcess);
      
      const duration = Date.now() - startTime;
      this.updateStats(syncResult, duration);
      this.adjustInterval(syncResult);
      
      this.stats.successfulPolls++;
      this.retryCount = 0;

      this.emit('sync:complete', {
        tel: this.tel,
        processed: syncResult.chatsProcessed,
        total: chatsToProcess.length,
        duration,
        messagesProcessed: syncResult.messagesProcessed
      });

    } catch (error) {
      const duration = Date.now() - startTime;
      this.stats.failedPolls++;
      this.retryCount++;

      this.emit('sync:error', { 
        tel: this.tel, 
        error: error instanceof Error ? error.message : String(error), 
        duration,
        retryCount: this.retryCount
      });

      // Handle retry logic
      if (this.retryCount <= this.config.maxRetries) {
        this.currentInterval = this.config.retryDelay;
        this.emit('sync:retry', { 
          tel: this.tel, 
          retryCount: this.retryCount,
          nextRetryIn: this.config.retryDelay
        });
      } else {
        this.emit('sync:failed', { 
          tel: this.tel, 
          error: error instanceof Error ? error.message : String(error),
          totalRetries: this.retryCount
        });
        this.retryCount = 0;
        this.currentInterval = this.config.interval;
      }

    } finally {
      this.isPolling = false;
    }
  }

  /**
   * Get chats to process based on strategy
   */
  private async getChatsToProcess(): Promise<string[]> {
    try {
      const chatsResponse = await this.client.chats.list({ tel: this.tel });
      const allChats = chatsResponse.data.chats;
      
      switch (this.config.strategy) {
        case 'all-chats':
          return allChats.map(chat => chat.id);
        
        case 'active-only':
          return this.getActiveChats(allChats);
        
        case 'unread-only':
          return allChats
            .filter(chat => chat.unreadCount > 0)
            .map(chat => chat.id);
        
        case 'smart':
          return this.getSmartChats(allChats);
        
        default:
          return allChats.map(chat => chat.id);
      }
    } catch (error) {
      this.emit('error', { context: 'getChatsToProcess', error: error instanceof Error ? error.message : String(error) });
      return [];
    }
  }

  /**
   * Get active chats (with recent activity)
   */
  private getActiveChats(chats: any[]): string[] {
    const cutoffTime = Date.now() - this.config.maxAge;
    
    return chats
      .filter(chat => {
        const activity = this.chatActivities.get(chat.id);
        return activity && activity.lastActivity.getTime() > cutoffTime;
      })
      .map(chat => chat.id);
  }

  /**
   * Get chats using smart algorithm
   */
  private getSmartChats(chats: any[]): string[] {
    const now = Date.now();
    const recentCutoff = now - (60 * 60 * 1000); // 1 hour
    const activeCutoff = now - this.config.maxAge;

    const prioritizedChats = chats
      .map(chat => {
        const activity = this.chatActivities.get(chat.id);
        let priority = 0;

        // Unread messages get highest priority
        if (chat.unreadCount > 0) {
          priority += 1000;
        }

        // Recent activity gets medium priority
        if (activity && activity.lastActivity.getTime() > recentCutoff) {
          priority += 500;
        }

        // General activity gets low priority
        if (activity && activity.lastActivity.getTime() > activeCutoff) {
          priority += activity.messageCount;
        }

        return { chatId: chat.id, priority };
      })
      .filter(item => item.priority > 0)
      .sort((a, b) => b.priority - a.priority)
      .slice(0, this.config.batchSize)
      .map(item => item.chatId);

    return prioritizedChats;
  }

  /**
   * Sync messages for specified chats
   */
  private async syncChats(chatIds: string[]): Promise<{ chatsProcessed: number; messagesProcessed: number }> {
    let chatsProcessed = 0;
    let messagesProcessed = 0;

    for (const chatId of chatIds) {
      try {
        const messagesResponse = await this.client.messages.list({
          tel: this.tel,
          chatId,
          limit: 50
        });

        if (messagesResponse && messagesResponse.messages.length > 0) {
          messagesProcessed += messagesResponse.messages.length;
          this.updateChatActivity(chatId, messagesResponse.messages.length);
          
          this.emit('chat:synced', {
            tel: this.tel,
            chatId,
            messageCount: messagesResponse.messages.length
          });
        }

        chatsProcessed++;

      } catch (error) {
        this.emit('chat:sync:error', {
          tel: this.tel,
          chatId,
          error: error instanceof Error ? error.message : String(error)
        });
      }
    }

    return { chatsProcessed, messagesProcessed };
  }

  /**
   * Update chat activity tracking
   */
  private updateChatActivity(chatId: string, messageCount: number): void {
    const existing = this.chatActivities.get(chatId);
    
    if (existing) {
      existing.lastActivity = new Date();
      existing.messageCount += messageCount;
    } else {
      this.chatActivities.set(chatId, {
        chatId,
        lastActivity: new Date(),
        messageCount,
        priority: 0
      });
    }
  }

  /**
   * Update polling statistics
   */
  private updateStats(result: { chatsProcessed: number; messagesProcessed: number }, duration: number): void {
    this.stats.chatsProcessed += result.chatsProcessed;
    this.stats.messagesProcessed += result.messagesProcessed;
    
    // Update average latency
    const totalLatency = this.stats.averageLatency * (this.stats.successfulPolls || 1);
    this.stats.averageLatency = (totalLatency + duration) / (this.stats.successfulPolls + 1);
  }

  /**
   * Adjust polling interval based on activity
   */
  private adjustInterval(result: { chatsProcessed: number; messagesProcessed: number }): void {
    if (!this.config.adaptiveInterval) {
      this.currentInterval = this.config.interval;
      return;
    }

    const baseInterval = this.config.interval;
    const activityScore = result.messagesProcessed / Math.max(result.chatsProcessed, 1);

    // Adjust interval based on activity
    if (activityScore > 10) {
      // High activity - poll more frequently
      this.currentInterval = Math.max(baseInterval * 0.5, 60000); // Min 1 minute
    } else if (activityScore > 5) {
      // Medium activity - slightly more frequent
      this.currentInterval = baseInterval * 0.75;
    } else if (activityScore < 1) {
      // Low activity - poll less frequently
      this.currentInterval = Math.min(baseInterval * 2, 30 * 60 * 1000); // Max 30 minutes
    } else {
      // Normal activity
      this.currentInterval = baseInterval;
    }

    this.emit('interval:adjusted', {
      tel: this.tel,
      oldInterval: this.config.interval,
      newInterval: this.currentInterval,
      activityScore
    });
  }

  /**
   * Calculate estimated time for next poll
   */
  private calculateEstimatedTime(): number {
    return Math.max(this.stats.averageLatency, 5000); // Min 5 seconds
  }

  /**
   * Start health check monitoring
   */
  private startHealthCheck(): void {
    this.healthCheckInterval = setInterval(async () => {
      await this.performHealthCheck();
    }, this.config.healthCheckInterval);
  }

  /**
   * Perform health check
   */
  private async performHealthCheck(): Promise<void> {
    try {
      const health = await this.client.health();
      
      if (!health || (health as any).success === false) {
        this.emit('health:unhealthy', { 
          tel: this.tel, 
          reason: 'api_unavailable' 
        });
      }

      // Check if we're stuck polling
      const now = Date.now();
      if (this.stats.lastPollAt) {
        const timeSinceLastPoll = now - this.stats.lastPollAt.getTime();
        const expectedMaxTime = this.currentInterval * 2;
        
        if (timeSinceLastPoll > expectedMaxTime) {
          this.emit('health:unhealthy', { 
            tel: this.tel, 
            reason: 'polling_stuck',
            timeSinceLastPoll
          });
        }
      }

    } catch (error) {
      this.emit('health:unhealthy', { 
        tel: this.tel, 
        reason: 'health_check_failed',
        error: error instanceof Error ? error.message : String(error)
      });
    }
  }

  /**
   * Force execute a poll immediately
   */
  async forcePoll(): Promise<void> {
    if (this.isPolling) {
      throw new Error('Poll already in progress');
    }

    this.emit('polling:forced', { tel: this.tel });
    await this.executePoll();
  }

  /**
   * Get current polling statistics
   */
  getStats(): PollingStats {
    return { ...this.stats };
  }

  /**
   * Get chat activities
   */
  getChatActivities(): ChatActivity[] {
    return Array.from(this.chatActivities.values());
  }

  /**
   * Update polling configuration
   */
  updateConfig(updates: Partial<PollingConfig>): void {
    this.config = { ...this.config, ...updates };
    
    if (updates.interval) {
      this.currentInterval = updates.interval;
    }

    this.emit('config:updated', { tel: this.tel, config: this.config });
  }

  /**
   * Reset statistics
   */
  resetStats(): void {
    this.stats = this.initializeStats();
    this.stats.isRunning = this.interval !== null;
    this.chatActivities.clear();
    
    this.emit('stats:reset', { tel: this.tel });
  }

  /**
   * Pause polling (temporarily stop without clearing state)
   */
  pause(): void {
    if (this.interval) {
      clearTimeout(this.interval);
      this.interval = null;
    }

    this.stats.isRunning = false;
    this.stats.nextPollAt = null;
    
    this.emit('polling:paused', { tel: this.tel });
  }

  /**
   * Resume polling from paused state
   */
  resume(): void {
    if (!this.stats.isRunning && this.config.enabled) {
      this.stats.isRunning = true;
      this.scheduleNextPoll();
      
      this.emit('polling:resumed', { tel: this.tel });
    }
  }
}